import { useEffect, useState } from "react";
import { Handshake } from "lucide-react";

// Module-level guard — survives React StrictMode's double-invoke of effects in dev
let _splashHandled = false;

/**
 * SplashScreen — brief welcome screen shown once per browser tab (sessionStorage).
 * heylpMATE name, animated handshake, green/blue/white brand palette, tagline.
 * Auto-dismisses after ~1.4s with a smooth fade-out.
 */
export default function SplashScreen() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    if (_splashHandled) return false;
    return sessionStorage.getItem("hm_splash_seen") !== "1";
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!show || _splashHandled) return;
    _splashHandled = true;
    sessionStorage.setItem("hm_splash_seen", "1");
    // Fire-and-forget: React 18 StrictMode may run this effect twice in dev, so we
    // intentionally do NOT return a cleanup that clears the timers — the module-level
    // guard above prevents duplicate scheduling, and setState after unmount is a
    // no-op that React just warns about (safe here).
    setTimeout(() => setFading(true), 1400);
    setTimeout(() => setShow(false), 1950);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!show) return null;

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 z-[9999] grid place-items-center transition-opacity duration-500 ${fading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      style={{
        background: "linear-gradient(135deg, #EAF7F2 0%, #FFF1EA 55%, #FFFFFF 100%)",
      }}
    >
      <div className="text-center px-6 hm-splash-rise">
        <div className="relative mx-auto w-24 h-24 md:w-28 md:h-28 grid place-items-center rounded-full shadow-lg"
             style={{ background: "linear-gradient(135deg, #087A5A, #00604A)" }}>
          <Handshake className="w-12 h-12 md:w-14 md:h-14 text-white hm-shake" strokeWidth={2.2} />
          <span className="absolute inset-0 rounded-full hm-pulse-ring" style={{ borderColor: "#087A5A" }} />
        </div>
        <h1 className="mt-6 font-display font-bold text-5xl md:text-7xl leading-none">
          <span style={{ color: "#F45118" }}>Heylp</span><span style={{ color: "#00604A" }}>Mate</span>
          <sup className="text-xs md:text-sm align-super text-neutral-500 font-sans">™</sup>
        </h1>
        <p className="mt-3 text-xs md:text-sm tracking-wide text-neutral-600">
          Real Human Assistance on Rent
        </p>
        <p className="mt-4 text-sm md:text-base tracking-wide text-neutral-600">
          Human Help. <b style={{ color: "#F45118" }}>Real People.</b> <b style={{ color: "#00604A" }}>Real Care.</b>
        </p>
        <div className="mt-8 mx-auto w-28 h-1 rounded-full overflow-hidden bg-black/5">
          <div className="h-full hm-splash-bar" style={{ background: "linear-gradient(90deg, #F45118, #00604A)" }} />
        </div>
        <a href="mailto:heylpmate@gmail.com?subject=Co-founder%20interest%20in%20HeylpMate"
           data-testid="splash-cofounder"
           className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm font-bold text-white shadow-lg"
           style={{ background: "linear-gradient(135deg, #F45118, #FF641F)" }}>
          ✨ Looking for a co-founder — reach out
        </a>
      </div>
    </div>
  );
}
